import { LAYOUT_NAV_ID } from './layoutServiceNavDuplicate';

export const NAV_ELEMENT_TAG = 'z-unified-nav-v2';

/**
 * True when this `z-unified-nav-v2` was mounted inside another one by accident.
 *
 * Upstream pages can replace a container's `innerHTML` with a whole
 * server-rendered document — a legacy AJAX action whose response is a redirect
 * to the full page, for example. The copy carries its own `z-unified-nav-v2`,
 * the browser upgrades it on insertion, and a second navigation mounts inside
 * the page content of the first one.
 *
 * The navigation is built for a single instance per document: `useSlotPortal`,
 * `useZdsMenuHost`, `useTopSpacing` and `useMainPageScroll` all resolve the nav
 * through document-wide selectors that return the first match, and
 * `NavigationRoot` keeps a head `<style>` keyed by a fixed id. A stray copy
 * therefore steals portals, measurements and teardown from the real one.
 *
 * Nesting is read off the ancestor chain rather than by counting elements,
 * because two instances may legitimately coexist as siblings: the
 * layout-service overlap, and the `ai-only` instance appended straight to
 * `document.body`.
 *
 * Nesting under the layout-service nav is deliberate and stays excluded. Layout
 * service wraps the app fragment, so an upstream that renders the element
 * itself lands inside it; there the upstream instance is the one that must
 * render, and `isLayoutServiceNavNestedDuplicate` steps the layout-service
 * instance down to suppressed chrome instead.
 */
export function isNestedNavInstance(element: Element): boolean {
  const ancestorNav = element.parentElement?.closest(NAV_ELEMENT_TAG);

  if (!ancestorNav) {
    return false;
  }

  return ancestorNav.id !== LAYOUT_NAV_ID;
}
